import { Button, Card, CardContent, FormField, Input, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useCreateBranch,
  useOwnerState,
  useUpdateBranch,
} from "../../features/backoffice/queries.ts";

export const Route = createFileRoute("/_app/branches")({ component: BranchesScreen });

function BranchesScreen() {
  const { data } = useOwnerState();
  const create = useCreateBranch();
  const update = useUpdateBranch();
  const rows = data?.branches ?? [];
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");

  const submitCreate = () => {
    if (!newName.trim()) return;
    create.mutate({ name: newName.trim(), address: newAddress.trim() || undefined });
    setNewName("");
    setNewAddress("");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Branches"
        subtitle="Create/edit/deactivate branch with restrictive feature overrides."
      />
      <Card>
        <CardContent className="grid gap-3 p-5 md:grid-cols-[1fr_1fr_auto]">
          <FormField id="newBranchName" label="Branch name">
            <Input
              id="newBranchName"
              placeholder="e.g. Ari Rooftop"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          </FormField>
          <FormField id="newBranchAddress" label="Address">
            <Input
              id="newBranchAddress"
              placeholder="e.g. Ari Soi 4, Bangkok"
              value={newAddress}
              onChange={(event) => setNewAddress(event.target.value)}
            />
          </FormField>
          <div className="flex items-end">
            <Button className="w-full" onClick={submitCreate} disabled={!newName.trim()}>
              Create branch
            </Button>
          </div>
        </CardContent>
      </Card>
      <DataTable
        rows={rows}
        getRowKey={(row) => row.id}
        columns={[
          {
            key: "name",
            header: "Name",
            cell: (row) => (
              <Input
                aria-label={`${row.name} name`}
                defaultValue={row.name}
                onBlur={(event) =>
                  event.target.value !== row.name &&
                  update.mutate({ branchId: row.id, input: { name: event.target.value } })
                }
              />
            ),
          },
          {
            key: "address",
            header: "Address",
            cell: (row) => (
              <Input
                aria-label={`${row.name} address`}
                defaultValue={row.address}
                onBlur={(event) =>
                  event.target.value !== row.address &&
                  update.mutate({ branchId: row.id, input: { address: event.target.value } })
                }
              />
            ),
          },
          { key: "hours", header: "Hours", cell: (row) => row.operatingHours },
          {
            key: "active",
            header: "State",
            cell: (row) => (
              <StatusChip tone={row.active ? "success" : "neutral"}>
                {row.active ? "active" : "inactive"}
              </StatusChip>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            cell: (row) => (
              <Button
                size="sm"
                variant="outline"
                className="text-foreground"
                onClick={() => update.mutate({ branchId: row.id, input: { active: !row.active } })}
              >
                {row.active ? "Deactivate" : "Activate"}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}
